<template>
  <div class="els-card-session">
    <div class="els-card-session__body">
      <div class="els-card-session__title-wrapper">
        <div class="els-card-session__title-group">
          <h2 class="els-card-session__title">
            {{ redeableStatus[sessionStatus] }}
            <LabStatusIcon
              class="els-card-session__status-icon"
              :status="sessionStatus"
            />
          </h2>
          <div class="els-card-session__subtitle">
            <p
              v-if="canShowSessionDuration"
              class="els-card-session__time-running"
            >
              TIME RUNNING: {{ formatRunningTime(runningTime) }}
            </p>
            <p>
              {{ helpTexts[sessionStatus] }}
            </p>
          </div>
        </div>
        <ELSCardGlobalSession
          class="els-card-session__session-details"
          v-if="canShowSessionDetails || isOtherSessionRunning || errorCode"
          :customError="errorCode"
          :currentLab="currentLab"
          :currentSession="currentSession"
          :labsRequestPayload="labsRequestPayload"
        />
      </div>
      <div class="els-card-session__actions-wrapper">
        <div class="els-card-session__actions" v-if="labHasStarted">
          <div class="els-card-session__actions__switcher">
            <span
              class="els-card-session__actions__switcher__text"
              :class="{
                'els-card-session__actions__switcher__text--disabled': isChangingState,
              }"
              >{{ switcherText }}</span
            >
            <SwitchCheckbox
              :disabled="isChangingState"
              v-model="isLabFinished"
            />
          </div>
        </div>
        <button
          class="btn btn--secondary els-card-session__btn"
          @click="handleSession"
          :disabled="
            isLoadingSession ||
              isOtherSessionRunning ||
              isTransitioningStates ||
              !!errorCode
          "
        >
          {{ ctaTexts[sessionStatus] }}
        </button>
      </div>
    </div>
    <DialogModal v-if="showIdleModal" @close-dialog-modal="closeModal">
      <p class="dialog-modal__title">Are you still working on this lab?</p>
      <div class="dialog-modal__actions-wrapper">
        <button class="btn btn--primary" @click="handlePingInterval">
          Yes
        </button>
        <button class="btn btn--secondary" @click="closeModal">No</button>
      </div>
    </DialogModal>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { mapState } from 'vuex'
import DialogModal from '@/components/modals/DialogModal.vue'
import CONSTANTS from '@/constants'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import ELSCardGlobalSession from '@/components/modals/lab-modal/ELSCardGlobalSession.vue'
import LabStatusIcon from '@/components/modals/lab-modal/LabStatusIcon.vue'

export default {
  components: {
    DialogModal,
    SwitchCheckbox,
    ELSCardGlobalSession,
    LabStatusIcon,
  },
  props: {
    currentLab: {
      type: Object,
      required: true,
    },
    currentSession: {
      type: Object,
      required: false,
    },
    setLabStatus: {
      type: Function,
      required: true,
    },
    mixpanelEventPayload: {
      type: Object,
      required: true,
    },
    labsRequestPayload: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      isLoadingLab: false,
      isLoadingSession: false,
      sessionRefreshIntervalId: null,
      sessionPingIntervalId: null,
      runningTime: null,
      runningTimeIntervalId: null,
      refreshSeconds: 5,
      pingSeconds: 60,
      showIdleModal: false,
      timeIdleModal: 0,
      markingActive: null,
      helpTexts: {
        iddle:
          'Start the lab and you’ll be able to download a VPN file and connect.',
        starting:
          'Start the lab and you’ll be able to download a VPN file and connect.',
        started: '',
        stopping: '',
        stopped: '',
        terminating: '',
        terminated: '',
      },
      redeableStatus: {
        iddle: 'Not running',
        starting: 'Lab Starting',
        started: 'Lab Running',
        stopping: 'Lab Stopping',
        stopped: 'Lab Stopped',
        terminating: 'Lab Stopped',
        terminated: 'Lab Stopped',
      },
      ctaTexts: {
        iddle: 'Start lab',
        starting: 'Start lab',
        started: 'Stop lab',
        stopping: 'Stop lab',
        stopped: 'Resume lab',
        terminating: 'Start lab',
        terminated: 'Start lab',
      },
      isChangingState: false,
      errorCode: 0,
    }
  },
  computed: {
    ...mapState({
      course: state => state.courses.currentCourse,
      activeCourses: state => state.courses.activeCourses,
    }),
    switcherText() {
      if (this.isLabFinished) {
        return 'Mark unfinished'
      }
      return 'Mark finished'
    },
    isLabFinished: {
      get() {
        return (
          this.currentLab.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
        )
      },
      set() {
        this.toggleLabStatus()
      },
    },
    sessionStatus() {
      return this.currentSession &&
        this.currentSession.status &&
        !this.sessionsToIgnore.includes(this.currentSession.status)
        ? this.currentSession.status
        : CONSTANTS.LABS.SESSION_STATUS.IDDLE
    },
    sessionsToIgnore() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.TERMINATING,
        CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        CONSTANTS.LABS.SESSION_STATUS.ERROR,
      ]
    },
    isInitialStatus() {
      return (
        !this.sessionStatus ||
        ['terminating', 'terminated', 'error'].includes(this.sessionStatus)
      )
    },
    labFinishedTime() {
      return format(
        new Date(this.currentLab.user_status_modified),
        'LLLL d, yyyy hh:mm a'
      )
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    globalRunningLabName() {
      return this.globalRunningSession && this.globalRunningSession.lab.name
    },
    isOtherSessionRunning() {
      const currentSessionId = this.currentSession
        ? this.currentSession.id
        : null
      return (
        this.globalRunningSession &&
        this.globalRunningSession.id !== currentSessionId
      )
    },
    shouldCreateRefreshInterval() {
      const sessionRefreshStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ]
      return sessionRefreshStatuses.includes(this.sessionStatus)
    },
    shouldCreatePingInterval() {
      const sessionPingStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
      ]
      return sessionPingStatuses.includes(this.sessionStatus)
    },
    activeCourse() {
      return !!this.activeCourses.find(
        activeCourse => activeCourse.id === this.course.id
      )
    },
    isTransitioningStates() {
      if (!this.currentSession) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    labHasStarted() {
      if (!this.currentLab) return false
      const userHasStartedStates = [
        CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        CONSTANTS.CONTENT_USER_STATUS.STARTED,
      ]
      return userHasStartedStates.includes(this.currentLab.user_status)
    },
    canShowSessionDetails() {
      if (!this.currentSession) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPED,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    canShowSessionDuration() {
      if (!this.currentSession) return false
      const sessionTransitioningState = [
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPED,
      ]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
  },
  methods: {
    formatRunningTime(secs) {
      const sec_num = parseInt(secs, 10)
      const hours = Math.floor(sec_num / 3600)
      const minutes = Math.floor(sec_num / 60) % 60
      const seconds = sec_num % 60
      return [hours, minutes, seconds]
        .map(v => (v < 10 ? `0${v}` : v))
        .join(':')
    },
    handleSession() {
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.IDDLE)
        this.createSession()
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED)
        this.updateSessionStatus(CONSTANTS.LABS.SESSION_STATUS.STOPPED)
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STOPPED)
        this.updateSessionStatus(CONSTANTS.LABS.SESSION_STATUS.STARTED)
    },
    async createSession() {
      this.isLoadingSession = true

      const response = await this.$store.dispatch(
        'labs/createLabSession',
        this.labsRequestPayload
      )

      if (
        this.currentLab.user_status === CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
      )
        this.setLabStatus('started')

      if (response && response.errorCode) {
        this.errorCode = response.errorCode
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_SESSION_ERROR, {
          ...this.mixpanelEventPayload,
          error_code: response.errorCode,
        })
      } else {
        this.isLoadingSession = false

        const excludedParentTypes = [
          CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST,
          CONSTANTS.PARENT_CONTENT_TYPES_KEYS.LEARNING_PATH,
        ]
        if (!excludedParentTypes.includes(this.context)) {
          setTimeout(async () => {
            this.markingActive = await this.markCourseAsActive()
          }, 15000)
        }
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_LAB_SESSION_STARTED,
          this.mixpanelEventPayload
        )
      }
    },
    async getSession() {
      await this.$store.dispatch('labs/getLabSession', {
        labId: this.currentLab.id,
        sessionId: this.currentSession.id,
        ...this.labsRequestPayload,
      })
    },
    async createPing() {
      const response = await this.$store.dispatch(
        'labs/createLabPing',
        this.labsRequestPayload
      )
      if (response) {
        // if error we should get Lab to update session if there is any, this will stop future pings with shouldCreatePingInterval
        await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)
      }
    },
    async updateSessionStatus(status) {
      this.isLoadingSession = true
      const response = await this.$store.dispatch(
        'labs/partialUpdateLabSession',
        {
          labId: this.currentLab.id,
          sessionId: this.currentSession.id,
          status,
          ...this.labsRequestPayload,
        }
      )
      await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)

      if (response && response.errorCode) {
        this.errorCode = response.errorCode
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_LAB_SESSION_ERROR, {
          ...this.mixpanelEventPayload,
          error_code: response.errorCode,
        })
      } else {
        this.isLoadingSession = false

        if (status === CONSTANTS.LABS.SESSION_STATUS.STARTED) {
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_STARTED,
            this.mixpanelEventPayload
          )
        } else if (status === CONSTANTS.LABS.SESSION_STATUS.STOPPED) {
          this.sendMixpanelEvent(
            this.eventsConstants.CONTENT_LAB_SESSION_STOPPED,
            this.mixpanelEventPayload
          )
        }
      }
    },
    async toggleLabStatus() {
      this.isChangingState = true
      await this.setLabStatus(
        this.isLabFinished
          ? CONSTANTS.CONTENT_USER_STATUS.STARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
      this.isChangingState = false
    },
    increaseRunningTime() {
      this.runningTime += 1
      this.timeIdleModal += 1
      this.checkIdleTime()
    },
    setInitialRunningTime() {
      this.runningTime = this.currentSession.running_time
    },
    handleRefreshInterval() {
      if (this.shouldCreateRefreshInterval && !this.sessionRefreshIntervalId) {
        this.sessionRefreshIntervalId = setInterval(
          this.getSession,
          this.refreshSeconds * 1000
        )
      } else if (!this.shouldCreateRefreshInterval) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    handlePingInterval() {
      if (this.shouldCreatePingInterval && !this.sessionPingIntervalId) {
        this.sessionPingIntervalId = setInterval(
          this.createPing,
          this.pingSeconds * 1000
        )
        this.timeIdleModal = 0
        this.showIdleModal = false
      } else if (!this.shouldCreatePingInterval) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
      }
    },
    handleRunningTimeInterval() {
      this.setInitialRunningTime()
      if (this.sessionStatus === 'started' && !this.runningTimeIntervalId) {
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.sessionStatus !== 'started') {
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    closeModal() {
      this.showIdleModal = false
      this.$emit('close-modal')
    },
    checkIdleTime() {
      if (this.timeIdleModal >= this.currentLab.duration_in_seconds * 2) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
        this.showIdleModal = true
      }
    },
    async markCourseAsActive() {
      if (!this.activeCourse) {
        await this.$store.dispatch('courses/addActiveCourse', this.course.id)
        await this.$store.dispatch('courses/getActiveCourses')
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_COURSE_ACTIVE_ADDED,
          {
            course_id: this.course.id,
            course_name: this.course.name,
            trigger: 'lab',
            ...this.course,
          }
        )
      }
    },
  },
  beforeDestroy() {
    this.sessionRefreshIntervalId = clearInterval(this.sessionRefreshIntervalId)
    this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.markingActive = clearTimeout(this.markingActive)
  },
  watch: {
    currentSession: {
      immediate: true,
      handler() {
        if (!this.currentSession) return
        this.handleRefreshInterval()
        this.handlePingInterval()
        this.handleRunningTimeInterval()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.els-card-session {
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
  &__body {
    text-align: left;
    display: flex;
    align-items: center;
    width: 100%;
  }
  &__title {
    font-size: 18px;
    line-height: 24px;
    color: var(--white);
    position: relative;
    text-transform: capitalize;
    margin: 0px 0px 5px;
    display: flex;
    align-content: center;
    align-items: center;
    &-wrapper {
      flex-grow: 1;
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      flex-direction: column;
    }
    &-group {
      width: 100%;
    }
  }
  &__subtitle {
    margin: 0;
    @include tablet {
      max-width: 80%;
    }
  }
  &__status-icon {
    margin-left: 10px;
  }
  &__lab-status {
    display: flex;
    align-items: center;
    margin-top: 20px;
  }
  &__btn {
    margin-left: auto;
    margin-top: auto;
  }
  &__actions {
    margin-left: auto;
    align-self: center;
    &-wrapper {
      display: flex;
      flex-direction: column;
      align-content: flex-start;
      height: 100%;
      justify-content: space-between;
      align-items: flex-end;
    }
    &__switcher {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: 30px;
      span {
        margin-right: 10px;
        display: inline-block;
        white-space: nowrap;
      }
      &__text {
        color: var(--white);
        text-transform: uppercase;
        line-height: 0;
        transition: 0.5s color ease;
        &--disabled {
          color: var(--light-blue-gray);
        }
      }
    }
  }
  &__time-running {
    color: var(--white);
    width: 100%;
  }
  &__flat-btn {
    background: transparent;
    color: var(--white);
    border: none;
    display: flex;
    align-items: center;
    padding: 0px;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      .els-card-session__flat-btn-text {
        color: var(--lighter-gray);
        border-color: var(--lighter-gray);
      }
    }
    &-text {
      font-size: 12px;
      font-weight: 700;
      border-bottom: 1px solid var(--white);
      margin: 0px 5px;
    }
  }
  &__check-icon {
    min-width: 16px;
    circle {
      fill: var(--lighter-gray);
    }
    &--success {
      circle {
        fill: var(--lab-status-green);
      }
    }
  }
  &__info-icon {
    color: var(--lighter-gray);
    font-size: 16px;
    margin-right: 5px;
  }
  &__session-details {
    margin-top: 30px;
  }
  @keyframes rotation {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(359deg);
    }
  }
  .modal-message {
    margin: auto;
  }
}
</style>
